import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { catchError, delay, map, mergeMap } from "rxjs/operators";
import { of } from "rxjs";
import { message } from "antd";
import i18next from "i18next";
import {
  ConversionApi,
  PipelineJobSummary,
  PipelineOverallStatus,
  ReconvertDeferredError,
} from "@/api/conversion.api";
import { RootState } from "@/store/types";

export interface PipelineStatusEntry {
  data: PipelineJobSummary | null; // null = no job exists yet for this file (not an error)
  loading: boolean;
  error?: string;
  fetchedAt?: number;
}

/**
 * In-flight reconvert request for one file -- drives the spinner/disabled state in the UI. Stays
 * loading across a deferred (409 retryable) round trip, since the epic re-asks on its own.
 */
export interface ReconvertEntry {
  loading: boolean;
  error?: string;
}

export interface ReconvertRequestPayload {
  baseFileId: string;
  /** Document version at upload time -- must match the StreamFile BaseMajorRev. */
  baseMajorRev: number;
  baseMinorRev?: number;
  /** Display name, used only for the toast messages. */
  fileName?: string;
  /** Internal: how many times this request has already been deferred by conversion-api. */
  attempt?: number;
}

export interface PipelineStatusState {
  byBaseFileId: Record<string, PipelineStatusEntry>;
  reconvertByBaseFileId: Record<string, ReconvertEntry>;
}

const initialState: PipelineStatusState = {
  byBaseFileId: {},
  reconvertByBaseFileId: {},
};

/**
 * conversion-api answers 409 retryable while a previous dispatch for the same job is still being
 * published. We re-ask automatically, but only a bounded number of times -- an unbounded loop would
 * keep a failed file retrying forever with no way for the user to see what went wrong.
 */
export const MAX_RECONVERT_DEFERRALS = 3;

/** Statuses for which "reconvert" is a meaningful action (the job is not already in flight). */
const RECONVERTIBLE_STATUSES = new Set<PipelineOverallStatus>([
  PipelineOverallStatus.Completed,
  PipelineOverallStatus.PartialSuccess,
  PipelineOverallStatus.Failed,
]);

const pipelineStatusSlice = createSlice({
  name: "PipelineStatus",
  initialState,
  reducers: {
    fetchPipelineStatus: (
      state,
      action: PayloadAction<{ baseFileId: string }>
    ) => {
      const { baseFileId } = action.payload;
      state.byBaseFileId[baseFileId] = {
        ...(state.byBaseFileId[baseFileId] ?? { data: null }),
        loading: true,
        error: undefined,
      };
    },
    pipelineStatusFetched: (
      state,
      action: PayloadAction<{
        baseFileId: string;
        data: PipelineJobSummary | null;
      }>
    ) => {
      const { baseFileId, data } = action.payload;
      state.byBaseFileId[baseFileId] = {
        data,
        loading: false,
        fetchedAt: Date.now(),
      };
    },
    pipelineStatusFailed: (
      state,
      action: PayloadAction<{ baseFileId: string; error: string }>
    ) => {
      const { baseFileId, error } = action.payload;
      state.byBaseFileId[baseFileId] = {
        data: state.byBaseFileId[baseFileId]?.data ?? null,
        loading: false,
        error,
        fetchedAt: Date.now(),
      };
    },
    clearPipelineStatus: (
      state,
      action: PayloadAction<{ baseFileId: string }>
    ) => {
      delete state.byBaseFileId[action.payload.baseFileId];
    },

    /**
     * Ask conversion-api to re-run the newest/active PipelineJob for this file revision. Both the
     * context-menu "Reconvert" item and the retry button on a failed status tag dispatch this, so
     * the deferral/retry/toast behaviour lives in one place (reconvertEpic) instead of being
     * duplicated per call site.
     */
    reconvertRequested: (
      state,
      action: PayloadAction<ReconvertRequestPayload>
    ) => {
      const { baseFileId } = action.payload;
      state.reconvertByBaseFileId[baseFileId] = {
        loading: true,
        error: undefined,
      };
    },
    reconvertSucceeded: (
      state,
      action: PayloadAction<{ baseFileId: string }>
    ) => {
      state.reconvertByBaseFileId[action.payload.baseFileId] = {
        loading: false,
      };
    },
    reconvertFailed: (
      state,
      action: PayloadAction<{ baseFileId: string; error: string }>
    ) => {
      const { baseFileId, error } = action.payload;
      state.reconvertByBaseFileId[baseFileId] = {
        loading: false,
        error,
      };
    },
  },
});

export const pipelineStatusActions = pipelineStatusSlice.actions;
export default pipelineStatusSlice.reducer;

export const fetchPipelineStatusEpic = (action$: any) =>
  action$.pipe(
    ofType(pipelineStatusActions.fetchPipelineStatus.type),
    mergeMap(
      (action: ReturnType<typeof pipelineStatusActions.fetchPipelineStatus>) => {
        const { baseFileId } = action.payload;
        console.debug(
          "[pipelineStatus.slice] fetchPipelineStatusEpic: fetching",
          baseFileId
        );
        return ConversionApi.getPipelineByBaseFileId(baseFileId).pipe(
          map((data) =>
            pipelineStatusActions.pipelineStatusFetched({ baseFileId, data })
          ),
          catchError((error) => {
            console.error(
              "[pipelineStatus.slice] fetchPipelineStatusEpic: failed",
              baseFileId,
              error
            );
            return of(
              pipelineStatusActions.pipelineStatusFailed({
                baseFileId,
                error: error?.message ?? "Something went wrong",
              })
            );
          })
        );
      }
    )
  );

/**
 * Re-runs the conversion for one file revision and refreshes its status tag straight away.
 *
 * A 409 "deferred" answer is not a failure: conversion-api is still publishing a previous dispatch
 * for the same job, so the request is re-issued after the server-advised delay (up to
 * MAX_RECONVERT_DEFERRALS times). A 404 means the file has never had a PipelineJob at all -- the
 * user must convert it once first, so it gets its own message rather than a generic error.
 */
export const reconvertEpic = (action$: any) =>
  action$.pipe(
    ofType(pipelineStatusActions.reconvertRequested.type),
    mergeMap(
      (action: ReturnType<typeof pipelineStatusActions.reconvertRequested>) => {
        const {
          baseFileId,
          baseMajorRev,
          baseMinorRev = 0,
          fileName,
          attempt = 0,
        } = action.payload;
        const name = fileName ?? baseFileId;

        console.debug("[pipelineStatus.slice] reconvertEpic: requesting", {
          baseFileId,
          baseMajorRev,
          baseMinorRev,
          attempt,
        });

        return ConversionApi.retryByFileRevision({
          baseFileId,
          baseMajorRev,
          baseMinorRev,
        }).pipe(
          mergeMap((job) => {
            console.debug("[pipelineStatus.slice] reconvertEpic: started", {
              baseFileId,
              jobId: job?.jobId,
            });
            message.success(
              i18next.t("contextmenumessages:reconvert_started", { name })
            );
            return of(
              pipelineStatusActions.reconvertSucceeded({ baseFileId }),
              // The job just went non-terminal again -- refresh the tag now instead of waiting
              // for the status cell next scheduled poll.
              pipelineStatusActions.fetchPipelineStatus({ baseFileId })
            );
          }),
          catchError((error) => {
            if (error instanceof ReconvertDeferredError) {
              if (attempt >= MAX_RECONVERT_DEFERRALS) {
                console.error(
                  "[pipelineStatus.slice] reconvertEpic: still deferred after max attempts",
                  baseFileId
                );
                message.error(
                  i18next.t("contextmenumessages:reconvert_failed", { name })
                );
                return of(
                  pipelineStatusActions.reconvertFailed({
                    baseFileId,
                    error: error.message,
                  })
                );
              }

              console.debug(
                "[pipelineStatus.slice] reconvertEpic: deferred, re-asking",
                baseFileId,
                error.retryAfterSeconds
              );
              message.info(
                i18next.t("contextmenumessages:reconvert_deferred", {
                  name,
                  seconds: error.retryAfterSeconds,
                })
              );
              return of(
                pipelineStatusActions.reconvertRequested({
                  ...action.payload,
                  attempt: attempt + 1,
                })
              ).pipe(delay(error.retryAfterSeconds * 1000));
            }

            console.error(
              "[pipelineStatus.slice] reconvertEpic: failed",
              baseFileId,
              error
            );
            message.error(
              error?.status === 404
                ? i18next.t("contextmenumessages:reconvert_no_job", { name })
                : i18next.t("contextmenumessages:reconvert_failed", { name })
            );
            return of(
              pipelineStatusActions.reconvertFailed({
                baseFileId,
                error: error?.message ?? "Something went wrong",
              })
            );
          })
        );
      }
    )
  );

export const pipelineStatusEpics = [fetchPipelineStatusEpic, reconvertEpic];

export const selectPipelineStatusByBaseFileId = (
  state: RootState,
  baseFileId?: string
): PipelineStatusEntry | undefined =>
  baseFileId ? state.pipelineStatus.byBaseFileId[baseFileId] : undefined;

export const selectReconvertByBaseFileId = (
  state: RootState,
  baseFileId?: string
): ReconvertEntry | undefined =>
  baseFileId
    ? state.pipelineStatus.reconvertByBaseFileId[baseFileId]
    : undefined;

export const selectAllPipelineStatuses = createSelector(
  (state: RootState) => state.pipelineStatus.byBaseFileId,
  (byBaseFileId) => byBaseFileId
);

export const selectAllReconvertStates = createSelector(
  (state: RootState) => state.pipelineStatus.reconvertByBaseFileId,
  (reconvertByBaseFileId) => reconvertByBaseFileId
);

/**
 * True when a reconvert would be accepted by conversion-api for this file: a job exists and is in a
 * terminal state (Failed/PartialSuccess/Completed). An unknown status (never fetched -- e.g. on the
 * Drawings/TeamDrives pages, which render no status column) is treated as allowed: the server is
 * the authority, and its 404/409 answers surface as messages rather than pre-emptively hiding the
 * action.
 */
export const isReconvertAllowed = (entry?: PipelineStatusEntry): boolean => {
  if (!entry || entry.data == null) return true;
  return RECONVERTIBLE_STATUSES.has(entry.data.overallStatus);
};
